import type { Witness } from '../store/booksStore'
import type { AnchoredRow } from '../types/anchoredRowTypes'

export interface LedgerSafePropagationOracleExportPayload {
  format_version: 'ledger-safe-propagation-oracle.v1'
  export_type: 'ledger_safe_propagation_oracle'
  exported_at: string
  chapter_id: string | null
  scope: {
    cluster_id: string | null
    label: string
    members: string[]
  } | null
  included_witness_ids: string[]
  excluded_witness_ids: string[]
  scope_witness_order: string[]
  included_witnesses: Witness[]
  excluded_witnesses: Witness[]
  context_rows: AnchoredRow[]
  oracle_rows: AnchoredRow[]
  excluded_oracle_rows: AnchoredRow[]
  summary: {
    total_rows: number
    total_scope_witnesses: number
    included_witness_count: number
    excluded_witness_count: number
  }
}

const filterRowsForWitnessIds = (
  rows: AnchoredRow[],
  witnessIds: Set<string>
): AnchoredRow[] => rows.map(row => ({
  ...row,
  unitMappings: row.unitMappings.filter(mapping => witnessIds.has(mapping.witnessId))
}))

export const buildLedgerSafePropagationOracleExport = ({
  chapterId,
  scope,
  scopeWitnesses,
  rows,
  excludedWitnessIds,
  exportedAt = new Date().toISOString()
}: {
  chapterId: string | null | undefined
  scope: {
    clusterId: string
    label: string
    members: string[]
  } | null
  scopeWitnesses: Witness[]
  rows: AnchoredRow[]
  excludedWitnessIds: string[]
  exportedAt?: string
}): LedgerSafePropagationOracleExportPayload => {
  const scopeWitnessOrder = scopeWitnesses.map(witness => witness.id)
  const excludedWitnessIdSet = new Set(excludedWitnessIds.filter(witnessId => scopeWitnessOrder.includes(witnessId)))
  const includedWitnesses = scopeWitnesses.filter(witness => !excludedWitnessIdSet.has(witness.id))
  const includedWitnessIdSet = new Set(includedWitnesses.map(witness => witness.id))
  const excludedWitnesses = scopeWitnesses.filter(witness => excludedWitnessIdSet.has(witness.id))

  return {
    format_version: 'ledger-safe-propagation-oracle.v1',
    export_type: 'ledger_safe_propagation_oracle',
    exported_at: exportedAt,
    chapter_id: chapterId || null,
    scope: scope
      ? {
        cluster_id: scope.clusterId,
        label: scope.label,
        members: scope.members
      }
      : null,
    included_witness_ids: includedWitnesses.map(witness => witness.id),
    excluded_witness_ids: excludedWitnesses.map(witness => witness.id),
    scope_witness_order: scopeWitnessOrder,
    included_witnesses: includedWitnesses,
    excluded_witnesses: excludedWitnesses,
    context_rows: filterRowsForWitnessIds(rows, includedWitnessIdSet),
    oracle_rows: rows.map(row => ({
      ...row,
      unitMappings: [...row.unitMappings]
    })),
    excluded_oracle_rows: filterRowsForWitnessIds(rows, excludedWitnessIdSet),
    summary: {
      total_rows: rows.length,
      total_scope_witnesses: scopeWitnesses.length,
      included_witness_count: includedWitnesses.length,
      excluded_witness_count: excludedWitnesses.length
    }
  }
}
